import { Box, Icon, Menu, MenuItem, SxProps, Theme } from "@outschool/backpack";
import { Enrollment, Section } from "@outschool/gql-backend-generated";
import { faGear, faMessage, faStar, fasStar } from "@outschool/icons";
import { useLocale, useTranslation } from "@outschool/localization";
import { activityPath, enrolledPath } from "@outschool/routes";
import { useSession } from "@outschool/ui-auth";
import { useNavigation } from "@outschool/ui-utils";
import React from "react";
import { useLocation } from "react-router";

import { isInReviewTimeWindow } from "../../../shared/Section";
import { shortenedLeaderName } from "../../../shared/User";
import {
  EnrollmentControl,
  getControlsToDisplay
} from "../enrolled/EnrollmentLine";
import ParentPauseOngoingButton from "../enrolled/ParentPauseOngoingButton";
import ParentTransferButton from "../enrolled/ParentTransferButton";
import ParentWithdrawButton from "../enrolled/ParentWithdrawButton";
import { ResumeOngoingEnrollment } from "../enrolling/ResumeOngoingEnrollment";
import Link from "../Link";
// @ts-ignore - JSX import
import MessageModal from "../MessageModal";
import ClassItemActionButton from "./ClassItemActionButton";

export type EnrollmentWithSection = Enrollment & {
  section: Section;
};

const BUTTON_INHERIT_SX: SxProps = {
  width: "100%",
  color: "inherit",
  textDecoration: "none !important",
  fontSize: "inherit",
  backgroundColor: "transparent",
  "&:hover:not(:disabled), &:focus:not(:disabled)": {
    backgroundColor: "rgba(0, 0, 0, 0.04) !important",
    color: "inherit !important"
  }
};

interface Props {
  enrollment: EnrollmentWithSection;
  completed?: boolean;
}

const ClassItemActions = ({ enrollment, completed }: Props) => {
  const { t } = useTranslation("client\\components\\ClassList");
  const location = useLocation();
  const { currentUser } = useSession();
  const navigate = useNavigation();
  const locale = useLocale();

  const moreButtonRef = React.useRef<HTMLButtonElement>(null);
  const [showMore, setShowMore] = React.useState(false);
  const [pauseOpen, setPauseOpen] = React.useState(false);
  const [withdrawOpen, setWithdrawOpen] = React.useState(false);

  const { section } = enrollment;
  const { activity, leader, currentUserPublicReview } = section;
  const canReview = !currentUserPublicReview && isInReviewTimeWindow(section);
  const canEdit = currentUserPublicReview?.currentUserCanEdit;

  const iconColor = "primary.main";

  const menuItems = React.useMemo(() => {
    if (completed) {
      return [];
    }
    if (activity.hasTeacherSchedule) {
      return [
        <MenuItem
          component={Link}
          to={enrolledPath(section.uid, locale)}
          sx={BUTTON_INHERIT_SX}
          onClick={(event: React.MouseEvent<HTMLElement>) => {
            event.stopPropagation();
          }}
          key="manage-meetings"
        >
          {t("Manage meetings")}
        </MenuItem>
      ];
    }

    const controlsToDisplay = getControlsToDisplay(enrollment, activity);
    return controlsToDisplay.map((control: EnrollmentControl) => {
      if (control === EnrollmentControl.Resume) {
        return (
          <MenuItem
            component={ResumeOngoingEnrollment as React.ElementType}
            variant="link"
            color="inherit"
            sx={BUTTON_INHERIT_SX}
            enrollment={enrollment}
            onClick={(event: React.MouseEvent<HTMLElement>) => {
              event.preventDefault();
            }}
            key="resume-ongoing"
          />
        );
      }
      if (control === EnrollmentControl.WithdrawOrStop) {
        return (
          <MenuItem
            component={ParentWithdrawButton as React.ElementType}
            variant="link"
            color="inherit"
            sx={BUTTON_INHERIT_SX}
            section={section}
            activity={activity}
            enrollment={enrollment}
            onClick={(event: React.MouseEvent<HTMLElement>) => {
              event.preventDefault();
            }}
            open={withdrawOpen}
            setOpen={setWithdrawOpen}
            key="withdraw"
          />
        );
      }
      if (control === EnrollmentControl.Pause) {
        return (
          <MenuItem
            component={ParentPauseOngoingButton as React.ElementType}
            variant="link"
            color="inherit"
            sx={BUTTON_INHERIT_SX}
            activity={activity}
            section={section}
            enrollment={enrollment}
            onClick={(event: React.MouseEvent<HTMLElement>) => {
              event.preventDefault();
            }}
            open={pauseOpen}
            setOpen={setPauseOpen}
            key="pause-ongoing"
          />
        );
      }
      if (control === EnrollmentControl.RequestTransfer) {
        return (
          <MenuItem
            component={ParentTransferButton as React.ElementType}
            variant="link"
            color="inherit"
            sx={BUTTON_INHERIT_SX}
            activity={activity}
            section={section}
            enrollment={enrollment}
            onClick={(event: React.MouseEvent<HTMLElement>) => {
              event.preventDefault();
            }}
            key="transfer"
          />
        );
      }
      return null;
    });
  }, [
    activity,
    enrollment,
    section,
    locale,
    t,
    pauseOpen,
    setPauseOpen,
    withdrawOpen,
    setWithdrawOpen,
    completed
  ]);

  return (
    <Box
      flex
      sx={(theme: Theme) => ({
        flexDirection: "row",
        gap: 20,
        minWidth: "18rem",
        justifyContent: "end",
        [theme.breakpoints.down("md")]: {
          width: "100%",
          justifyContent: "space-evenly"
        }
      })}
    >
      {completed ? (
        <ClassItemActionButton
          icon={
            <Icon
              icon={canEdit || currentUserPublicReview ? fasStar : faStar}
              fill={iconColor}
              sx={{ color: "primary.main" }}
            />
          }
          disabled={!canReview && !canEdit}
          onClick={e => {
            e.preventDefault();
            const activityWithSlug = {
              uid: activity.uid,
              slug_id: activity.slug_id,
              title: activity.title,
              isClub: activity.isClub
            };
            navigate(
              `${activityPath(activityWithSlug, true)}/feedback?sectionUid=${
                section.uid
              }`
            );
          }}
          text={
            canEdit || currentUserPublicReview
              ? t("Edit Review")
              : t("Add Review")
          }
        />
      ) : (
        <>
          <MessageModal
            renderButton={(open: () => void) => (
              <ClassItemActionButton
                icon={<Icon icon={faMessage} fill={iconColor} />}
                text={t("Message")}
                onClick={open}
              />
            )}
            user={currentUser}
            location={location}
            requireLogin={true}
            isLoggedIn
            viewName="messageToLeader"
            heading={t("Message to {{teacherName}}", {
              teacherName: shortenedLeaderName(leader.name)
            })}
            recipients={[leader]}
            activityUid={activity.uid}
            sectionUid={section && section.uid}
            showFromAndTo
            subject={t("Message about: {{activityTitle}}", {
              activityTitle: activity.title
            })}
            context={{
              action: "Enrolled parent to teacher",
              activityTitle: activity.title,
              subject: "Message about: " + activity.title
            }}
          />
          <ClassItemActionButton
            ref={moreButtonRef}
            icon={<Icon icon={faGear} fill={iconColor} />}
            text={t("Manage")}
            onClick={e => {
              e.preventDefault();
              setShowMore(!showMore);
            }}
          />
          <Menu
            anchorEl={moreButtonRef.current}
            open={showMore}
            onClose={(e: React.MouseEvent<HTMLElement>) => {
              e.stopPropagation();
              setShowMore(false);
            }}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center"
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "center"
            }}
          >
            {menuItems}
          </Menu>
        </>
      )}
    </Box>
  );
};

export default ClassItemActions;
